import Favorite from "@mui/icons-material/Favorite";
import { Alert, Button, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useFlag } from "../../../../server/optimizely";
import { useSupabaseLoader } from "../../../../server/supabase/hooks";
import { toAnalyticsIdSegment } from "../../../../utils/analytics-id";
import type { UUID } from "../../../../utils/uuid";
import { EmptyState } from "../generic/empty-state";
import { EngagementBreakdownData } from "./engagement-breakdown-data";
import { useEngagementBreakdownFields } from "./use-engagement-breakdown-fields";

type FieldKey = string;

export const AnalyticsSendsTableExpandedEngagementBreakdown: FC<{
  momentGroupingId: UUID | null;
  groupingInfoType: string;
}> = ({ momentGroupingId, groupingInfoType }) => {
  if (groupingInfoType === "person") {
    return (
      <EmptyState
        symbol={<Favorite fontSize="large" />}
        title="Engagement breakdown not available for automated events"
        subtitle="As Communications based on automated events are triggered whenever an event happens for an individual, we don't track engagement"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.active,
          borderRadius: 1,
          px: 4,
          py: 8,
        })}
      />
    );
  }

  return <AnalyticsSendsTableExpandedEngagementBreakdownData momentGroupingId={momentGroupingId} />;
};

const AnalyticsSendsTableExpandedEngagementBreakdownData: FC<{
  momentGroupingId: UUID | null;
}> = ({ momentGroupingId }) => {
  const [fieldKey, setFieldKey] = useState<FieldKey>();
  const {
    data: fieldsData,
    error: fieldsError,
    loading: fieldsLoading,
  } = useEngagementBreakdownFields();
  const [insightsConfigEnabled] = useFlag("cha-4301-ai-insights-configuration");

  const selectedField = useMemo(() => {
    const selected = fieldsData.find((field) => field.field_key === fieldKey);
    return selected ?? fieldsData[0];
  }, [fieldKey, fieldsData]);

  const {
    data: breakdown,
    loading,
    error: breakdownError,
  } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!momentGroupingId || !selectedField) return [];

      const { data, error } = await supabase.rpc("moment_engagement_breakdown", {
        field_key_: selectedField.field_key,
        moment_grouping_id_: momentGroupingId,
      });

      if (error) throw new Error("Unable to load engagement breakdown");

      return (data ?? []).map(({ clicked, delivered, label, percentage }) => ({
        clicked,
        label,
        percentage,
        sent: delivered,
      }));
    },
    [momentGroupingId, selectedField],
  );
  const error = fieldsError || breakdownError;

  if (!momentGroupingId || (!fieldsLoading && !selectedField && !fieldsError)) return null;

  return (
    <Stack spacing={3} direction="column">
      <Typography variant="body2">
        Engagement breakdowns are available for Communications containing links that are sent to
        multiple groups, providing insight into how they engaged with your Communication.{" "}
        {insightsConfigEnabled && (
          <>
            You can create groups to compare by{" "}
            <Link
              href="/manage/employees/insights_key_fields"
              data-analytics-id="engagement-breakdown-key-fields-link"
            >
              assigning key fields
            </Link>
            .
          </>
        )}
      </Typography>
      <Typography variant="body2">
        Engagement is calculated as the number of people in each group successfully received the
        message <strong>and</strong> interacted with any link within it.
      </Typography>
      {error && <Alert severity="error">Unable to load engagement breakdown</Alert>}
      {fieldsLoading && <CircularProgress size={24} />}
      {!fieldsLoading && fieldsData && !error && (
        <>
          <Stack direction="row" spacing={1}>
            {fieldsData.map((field) => (
              <Button
                key={field.field_key}
                variant={field.field_key === selectedField?.field_key ? "contained" : "outlined"}
                onClick={() => setFieldKey(field.field_key)}
                sx={{ borderRadius: 999 }}
                data-analytics-id={`engagement-breakdown-dimension-${toAnalyticsIdSegment(field.display_name)}`}
              >
                {field.display_name}
              </Button>
            ))}
          </Stack>
          {loading && <CircularProgress size={24} />}
        </>
      )}
      {!loading && !error && (
        <>
          {breakdown && breakdown.length > 1 ? (
            <EngagementBreakdownData breakdown={breakdown ?? []} />
          ) : (
            <EmptyState
              symbol={<Favorite fontSize="large" />}
              title={breakdown.length < 2 ? "Not enough recipient groups" : "No engagement yet"}
              subtitle={
                breakdown.length < 2
                  ? "Engagement breakdowns only appear for Communications with recipients in at least two groups."
                  : "This may be because the Communication doesn’t contain links. Check back later if you’re expecting to see some data here."
              }
              sx={(theme) => ({
                backgroundColor: theme.palette.background.active,
                borderRadius: 1,
                px: 4,
                py: 8,
              })}
            />
          )}
        </>
      )}
    </Stack>
  );
};
